import { Button, cn } from '@commander/ui';
import { RiArrowLeftLine, RiArrowRightLine } from '@remixicon/react';
import type React from 'react';
import type { StepNavigationProps } from '../../types/onboarding.types';

const StepNavigation: React.FC<StepNavigationProps> = ({
  currentStep,
  totalSteps,
  onBack,
  onNext,
  onSkip,
  isLoading,
  canProceed,
  showSkip = false,
  nextButtonText,
}) => {
  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;

  const getButtonText = () => {
    if (nextButtonText) {
      return nextButtonText;
    }

    if (isLastStep) {
      return 'Finish';
    }

    return (
      <>
        Next
        <RiArrowRightLine className="w-4 h-4" />
      </>
    );
  };

  const backButtonClassName = cn(
    'px-5 py-3 rounded-[360px] text-nav-text font-medium transition-colors duration-300',
    isFirstStep ? 'bg-[#E1EDEC] text-black cursor-not-allowed' : 'bg-[#E1EDEC] text-primary'
  );

  const nextButtonClassName = cn(
    'px-5 py-3 rounded-[360px] text-nav-text font-medium transition-colors duration-300',
    !canProceed
      ? 'bg-primary-400 text-primary cursor-not-allowed'
      : 'bg-primary hover:bg-primary-700 text-white'
  );

  return (
    <div className="flex justify-end items-center gap-4 mt-4">
      {/* Skip button - only show if enabled */}
      {showSkip && onSkip && (
        <Button
          onClick={onSkip}
          variant="outline"
          className="rounded-full bg-white text-gray-500 px-5 py-3"
          disabled={isLoading}
        >
          Skip
        </Button>
      )}

      {/* Back button */}
      <Button
        onClick={onBack}
        disabled={isFirstStep || isLoading}
        variant="outline"
        className={backButtonClassName}
      >
        <RiArrowLeftLine className="w-4 h-4" />
        Back
      </Button>

      {/* Next/Finish button */}
      <Button onClick={onNext} disabled={!canProceed || isLoading} className={nextButtonClassName}>
        {isLoading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 bg-primary border-white mr-2" />
            {isLastStep ? 'Finishing...' : 'Saving...'}
          </>
        ) : (
          getButtonText()
        )}
      </Button>
    </div>
  );
};

export default StepNavigation;
